import { z } from 'zod';
import { brandConversionDefinitionQualityTierSchema } from './brand-conversion-definition.js';
// Funnel-intake schemas — the chat-based onboarding Mag runs when a
// brand's conversion funnel is first detected. She interviews the founder
// to learn what each step IS (the payment gate, what's collected before
// pay, what's required to compute the price, what's deferrable) and
// confirms the step order. Models a multi-turn conversation, one question
// per turn, until she has enough to commit the funnel semantics.
//
// Pattern mirrors channel-intake (intakeMessageSchema +
// intakeAgentResultSchema + intakeStateSchema) so the chat drawer reuses
// the same bubble layout + submit semantics.
/** One turn in the funnel intake conversation. Same shape as the channel
 *  intake message so the chat drawer renders both without forking. */
export const funnelIntakeMessageSchema = z.object({
    role: z.enum(['mag', 'founder']),
    body: z.string().min(1).max(4000),
    createdAt: z.string().datetime(),
});
/** What a funnel step IS — its function in the flow. This is the
 *  semantic Mag is blind to today (steps are bare event names), and the
 *  thing that lets her reason about MOVING a gate (e.g. "request_paid is
 *  the payment_gate; the info_collection steps after it are deferrable"). */
export const funnelStepRoleSchema = z.enum([
    /** Awareness / entry — landing, first interaction. */
    'top_of_funnel',
    /** The user picks what they want (product, plan, service). */
    'selection',
    /** We collect info from the user (details, address, preferences). */
    'info_collection',
    /** The money / hard-commitment step (pay, submit-paid-request). */
    'payment_gate',
    /** The win — the conversion we optimize toward. */
    'final_conversion',
    /** Doesn't fit the above; Mag explains in the label. */
    'other',
]);
/** Per-step semantic understanding, founder-confirmed during intake. */
export const funnelStepSemanticSchema = z.object({
    /** The conversion-event name — matches a step in the funnel definition. */
    name: z.string().min(1).max(120),
    role: funnelStepRoleSchema,
    /** Human label in Mag's words — "product selection", "checkout", etc. Roomy
     *  enough for a descriptive clause (e.g. flow caveats) without truncation. */
    label: z.string().min(1).max(160),
    /** Could this step happen AFTER payment without breaking the flow? The
     *  key signal for "move the payment gate earlier". */
    deferrableAfterPayment: z.boolean(),
    /** Is the data collected here required to COMPUTE the price/charge? If
     *  so it CANNOT move after payment — the guardrail against an invalid
     *  reorder. */
    requiredToComputePrice: z.boolean(),
});
/** Which funnel steps a given FLOW auto-skips. When a property (e.g.
 *  `wizard_type`) splits the funnel into flows, some flows can bypass a step —
 *  a deep-link that auto-selects a product advances the user past the
 *  selection screen, so that step's event NEVER fires for them. Without this,
 *  a skipped step reads as a 100% "drop" for that flow when those users are
 *  actually progressing — the cohort artifact that produces confidently-wrong
 *  recommendations. Founder-confirmed during intake. */
export const funnelFlowApplicabilitySchema = z.object({
    /** The conversion_props key whose values mark the distinct flows. */
    property: z.string().min(1).max(120),
    /** Per flow-value, the funnel steps it AUTO-SKIPS (event can't fire there). */
    skips: z
        .array(z.object({
        value: z.string().min(1).max(200),
        steps: z.array(z.string().min(1).max(120)).min(1).max(8),
    }))
        .max(12),
});
/** The finalized, founder-confirmed funnel understanding. Persisted on
 *  brand_funnel_definitions.semantics and read by the structural-
 *  recommendation agent (Phase B). */
export const funnelSemanticsSchema = z.object({
    steps: z.array(funnelStepSemanticSchema).min(2).max(8),
    /** The step that is the payment / hard-commitment gate, or null when
     *  the funnel has no payment step (e.g. a lead-gen funnel). */
    paymentGateStep: z.string().max(120).nullable(),
    /** Ordering constraints the founder named — legal/technical reasons a
     *  step can't move ("we need the address before payment to compute
     *  shipping"). Free text; null when none. */
    orderingConstraints: z.string().max(1000).nullable().default(null),
    /** Flows that auto-skip steps (deep-link / auto-select). null when every
     *  flow goes through every step (or there's only one flow). Read by the
     *  segmentation so a skipped step isn't mistaken for a drop. */
    flowApplicability: funnelFlowApplicabilitySchema.nullable().default(null),
    /** Mag's distilled read of the funnel + its business logic, her words.
     *  Threaded to the diagnosis agent so it doesn't re-derive context. */
    summary: z.string().min(1).max(2000),
    /** How strong a signal the WIN is — the founder's own answer, captured
     *  during this intake. [spec f1b4a30e S2]
     *
     *  WHY IT LIVES HERE AND NOT ON EACH STEP. There is exactly one win, and the
     *  tier is a property of it. A per-step tier would invite a tier on
     *  `wizard_started`, where it means nothing, and then something would have to
     *  decide which step's tier counts — re-creating the two-readers defect S1
     *  exists to fix. It sits beside `paymentGateStep` because that field is the
     *  same shape of claim: one named fact about the funnel as a whole.
     *
     *  NULLABLE, AND THE NULL IS LOAD-BEARING. `brands.conversion_definition`
     *  requires a tier, and the tier is NOT derivable — nothing about the string
     *  "account_created" says whether it is a soft lead or a sale, and the scorer
     *  weights them differently. So null means "the founder has not said", which
     *  WITHHOLDS the definition write and leaves mag's existing ask standing.
     *  A guessed tier would instead be scored as though the founder chose it.
     *
     *  Defaulted so the semantics rows stored before this field existed stay
     *  valid without a migration. */
    winQualityTier: brandConversionDefinitionQualityTierSchema.nullable().default(null),
});
/** Per-turn output from the funnel-intake agent. Discriminated on
 *  decision: 'ask' = keep interviewing, 'propose' = Mag has enough to
 *  present the funnel semantics for the founder to confirm. */
export const funnelIntakeAgentResultSchema = z.discriminatedUnion('decision', [
    z.object({
        decision: z.literal('ask'),
        /** Mag's next question, her voice, lowercase, ≤4000 chars. */
        reply: z.string().min(1).max(4000),
    }),
    z.object({
        decision: z.literal('propose'),
        /** Mag's closing line — "ok, here's how i read your funnel. confirm
         *  and i'll factor it into every recommendation." */
        reply: z.string().min(1).max(4000),
        /** The funnel semantics the founder confirms. */
        proposal: funnelSemanticsSchema,
    }),
]);
/** State persisted on brand_funnel_definitions.intake_state — transcript
 *  + latest agent result so a refresh resumes mid-conversation. Null when
 *  no intake started. After finalize, completed_at is stamped; the row
 *  stays as a receipt + re-intake context. */
export const funnelIntakeStateSchema = z.object({
    messages: z.array(funnelIntakeMessageSchema).min(1).max(40),
    latest_result: funnelIntakeAgentResultSchema.nullable(),
    started_at: z.string().datetime(),
    completed_at: z.string().datetime().nullable().default(null),
});
//# sourceMappingURL=funnel-intake.js.map